import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { api } from "../../lib/api";

interface Cat {
  id: string;
  name: string;
  motto: string | null;
  active: boolean;
  missing: boolean;
  smsEnabled: boolean;
  photos: { id: string; url: string; isPrimary: boolean }[];
  unreadFindings: number;
}

export function Dashboard() {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const cats = useQuery({ queryKey: ["cats"], queryFn: () => api<Cat[]>("/api/pets") });

  // Lokale Reihenfolge fürs Drag&Drop; aus den Serverdaten gespiegelt.
  const [order, setOrder] = useState<Cat[]>([]);
  useEffect(() => {
    if (cats.data) setOrder(cats.data);
  }, [cats.data]);

  const dragIndex = useRef<number | null>(null);
  const [dragging, setDragging] = useState<number | null>(null);

  const persist = useMutation({
    mutationFn: (ids: string[]) =>
      api("/api/pets/reorder", { method: "POST", body: JSON.stringify({ ids }) }),
  });

  const onDragStart = (i: number) => {
    dragIndex.current = i;
    setDragging(i);
  };
  const onDragEnter = (i: number) => {
    const from = dragIndex.current;
    if (from === null || from === i) return;
    setOrder((cur) => {
      const next = [...cur];
      const [moved] = next.splice(from, 1);
      if (!moved) return cur;
      next.splice(i, 0, moved);
      return next;
    });
    dragIndex.current = i;
    setDragging(i);
  };
  const onDragEnd = () => {
    dragIndex.current = null;
    setDragging(null);
    // Neue Reihenfolge sichern + Cache angleichen (kein Refetch-Flackern).
    qc.setQueryData(["cats"], order);
    persist.mutate(order.map((c) => c.id));
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h1 className="font-display text-3xl font-medium text-[#1B0E16]">{t("nav.cats")}</h1>
        <Link to="/app/pets/new" className="btn-primary">+ {t("cats.new")}</Link>
      </div>

      {cats.isLoading && <div className="text-slate-500">{t("common.loading")}</div>}
      {cats.data && cats.data.length === 0 && (
        <div className="card p-8 text-center text-[#6E5E66]">
          <div className="text-5xl mb-2">🐾</div>
          Lege dein erstes Haustier an.
        </div>
      )}
      {order.length > 0 && (
        <>
          {order.length > 1 && (
            <p className="mb-3 text-xs text-slate-400">{t("cats.reorderHint")}</p>
          )}
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            {order.map((c, i) => {
              const p = c.photos.find((x) => x.isPrimary) ?? c.photos[0];
              return (
                <Link
                  key={c.id}
                  to={`/app/pets/${c.id}`}
                  draggable
                  onDragStart={(e) => {
                    e.dataTransfer.effectAllowed = "move";
                    onDragStart(i);
                  }}
                  onDragEnter={() => onDragEnter(i)}
                  onDragOver={(e) => e.preventDefault()}
                  onDragEnd={onDragEnd}
                  className={`card overflow-hidden transition-shadow hover:shadow-md ${
                    dragging === i ? "opacity-50 ring-2 ring-brand-400" : ""
                  }`}
                >
                  <div className="aspect-square bg-[#F1E7EE] relative">
                    {p ? (
                      <img src={p.url} alt="" className="w-full h-full object-cover" draggable={false} />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center text-6xl">🐾</div>
                    )}
                    {c.missing && (
                      <div className="absolute top-2 left-2 rounded bg-red-600 px-2 py-0.5 text-xs font-semibold text-white">
                        {t("cats.missingBadge")}
                      </div>
                    )}
                    {!c.active && (
                      <div className="absolute top-2 right-2 bg-slate-900/80 text-white text-xs px-2 py-0.5 rounded">
                        {t("cats.inactiveBadge")}
                      </div>
                    )}
                  </div>
                  <div className="p-4">
                    <div className="font-display text-lg font-medium text-[#1B0E16]">{c.name}</div>
                    {c.motto && <div className="text-sm text-[#6E5E66] italic">"{c.motto}"</div>}
                  </div>
                </Link>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
}
